import { miaolinDemoTitle, primaryFormId } from '@/lib/mock-data'
import type { AnswerValue, PublicForm } from '@/types/form'

export type MiaolinCustomerCheckMode = 'new_customer' | 'existing_customer_change'

export type MiaolinDuplicateFieldMatch = {
  fieldKey: 'customerShortName' | 'companyFullName' | 'ownerName' | 'contactName'
  label: string
  questionId: string
  submittedValue: string
  existingValue: string
}

export type MiaolinExistingLookupFieldMatch = {
  fieldKey: 'existingCustomerCode'
  label: string
  submittedValue: string
  existingValue: string
  matched: boolean
}

export type MiaolinCustomerCheckResult =
  | {
      status: 'not_applicable'
      mode: null
      message: string
    }
  | {
      status: 'clear'
      mode: 'new_customer'
      message: string
    }
  | {
      status: 'duplicate_found'
      mode: 'new_customer'
      message: string
      matchedFields: MiaolinDuplicateFieldMatch[]
      existingCustomer: MiaolinMockCustomerRecord
    }
  | {
      status: 'existing_found'
      mode: 'existing_customer_change'
      message: string
      lookupValue: string
      lookupFields: MiaolinExistingLookupFieldMatch[]
      existingCustomer: MiaolinMockCustomerRecord
    }
  | {
      status: 'existing_missing'
      mode: 'existing_customer_change'
      message: string
      lookupValue: string
      lookupFields: MiaolinExistingLookupFieldMatch[]
    }

export type MiaolinMockCustomerRecord = {
  id: string
  customerShortName: string
  companyFullName: string
  ownerName: string
  contactName: string
  existingCustomerCode: string
}

const miaolinMockCustomers: MiaolinMockCustomerRecord[] = [
  {
    id: 'miaolin-mock-existing-test',
    customerShortName: 'test',
    companyFullName: '12345678test',
    ownerName: 'miao1',
    contactName: 'miao2',
    existingCustomerCode: '123test',
  },
]

const newCustomerDuplicateFields: Array<
  Pick<MiaolinDuplicateFieldMatch, 'fieldKey' | 'label' | 'questionId'> & {
    customerField: keyof MiaolinMockCustomerRecord
  }
> = [
  {
    fieldKey: 'customerShortName',
    label: '客戶簡稱',
    questionId: 'q-miaolin-06',
    customerField: 'customerShortName',
  },
  {
    fieldKey: 'companyFullName',
    label: '統編抬頭（公司全名）',
    questionId: 'q-miaolin-07',
    customerField: 'companyFullName',
  },
  {
    fieldKey: 'ownerName',
    label: '負責人',
    questionId: 'q-miaolin-08',
    customerField: 'ownerName',
  },
  {
    fieldKey: 'contactName',
    label: '聯絡人',
    questionId: 'q-miaolin-09',
    customerField: 'contactName',
  },
]

function answerAsString(value: AnswerValue | undefined) {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return value.map(String).join('、')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function normalizeMiaolinLookupValue(value: AnswerValue | string | undefined) {
  return answerAsString(value).trim().toLocaleLowerCase()
}

function getAnswer(answers: Map<string, AnswerValue>, questionId: string) {
  return answerAsString(answers.get(questionId)).trim()
}

function isMiaolinCustomerWorkflow(form: PublicForm) {
  return (
    form.id === primaryFormId ||
    form.title === miaolinDemoTitle ||
    form.settings.customerWorkflow === 'customer_onboarding'
  )
}

function parseExistingCustomerLookup(existingCustomerCodeValue: string) {
  const existingCustomerCode = existingCustomerCodeValue.trim()
  return {
    existingCustomerCode,
    lookupValue: existingCustomerCode,
  }
}

function buildExistingLookupFields(
  lookup: ReturnType<typeof parseExistingCustomerLookup>,
  customer: MiaolinMockCustomerRecord | undefined,
): MiaolinExistingLookupFieldMatch[] {
  return [
    {
      fieldKey: 'existingCustomerCode',
      label: '既有客戶代號',
      submittedValue: lookup.existingCustomerCode,
      existingValue: customer?.existingCustomerCode ?? '',
      matched: Boolean(
        customer &&
          normalizeMiaolinLookupValue(lookup.existingCustomerCode) ===
            normalizeMiaolinLookupValue(customer.existingCustomerCode),
      ),
    },
  ]
}

function findExistingCustomerByLookup(lookup: ReturnType<typeof parseExistingCustomerLookup>) {
  if (!lookup.existingCustomerCode) {
    return undefined
  }
  return miaolinMockCustomers.find(
    (customer) =>
      normalizeMiaolinLookupValue(customer.existingCustomerCode) ===
      normalizeMiaolinLookupValue(lookup.existingCustomerCode),
  )
}

function findExistingCustomerByCodeForFieldPreview(existingCustomerCode: string) {
  const normalizedLookupValue = normalizeMiaolinLookupValue(existingCustomerCode)
  if (!normalizedLookupValue) return undefined
  return miaolinMockCustomers.find(
    (customer) =>
      normalizeMiaolinLookupValue(customer.existingCustomerCode) === normalizedLookupValue,
  )
}

function checkNewCustomer(answers: Map<string, AnswerValue>): MiaolinCustomerCheckResult {
  const matchedFields = miaolinMockCustomers.flatMap((customer) =>
    newCustomerDuplicateFields.flatMap((field) => {
      const submittedValue = getAnswer(answers, field.questionId)
      if (!submittedValue) return []
      const existingValue = String(customer[field.customerField] ?? '')
      if (
        normalizeMiaolinLookupValue(submittedValue) !== normalizeMiaolinLookupValue(existingValue)
      ) {
        return []
      }
      return [
        {
          fieldKey: field.fieldKey,
          label: field.label,
          questionId: field.questionId,
          submittedValue,
          existingValue,
        },
      ]
    }),
  )

  if (matchedFields.length === 0) {
    return {
      status: 'clear',
      mode: 'new_customer',
      message: '未在 synthetic 客戶資料中找到重複欄位。',
    }
  }

  return {
    status: 'duplicate_found',
    mode: 'new_customer',
    message: '已在 synthetic 客戶資料中找到可能重複欄位。',
    matchedFields,
    existingCustomer: miaolinMockCustomers[0],
  }
}

function checkExistingCustomerChange(
  answers: Map<string, AnswerValue>,
): MiaolinCustomerCheckResult {
  const lookup = parseExistingCustomerLookup(getAnswer(answers, 'q-miaolin-03'))
  const existingCustomer = findExistingCustomerByLookup(lookup)
  if (existingCustomer) {
    return {
      status: 'existing_found',
      mode: 'existing_customer_change',
      message: '已在 synthetic 客戶資料中找到既有客戶。',
      lookupValue: lookup.lookupValue,
      lookupFields: buildExistingLookupFields(lookup, existingCustomer),
      existingCustomer,
    }
  }

  const previewCustomer = findExistingCustomerByCodeForFieldPreview(lookup.existingCustomerCode)
  return {
    status: 'existing_missing',
    mode: 'existing_customer_change',
    message: '找不到客戶資料，請確認既有客戶代號。',
    lookupValue: lookup.lookupValue,
    lookupFields: buildExistingLookupFields(lookup, previewCustomer),
  }
}

export function checkMiaolinCustomerSubmission(
  form: PublicForm,
  answers: Map<string, AnswerValue>,
): MiaolinCustomerCheckResult {
  if (!isMiaolinCustomerWorkflow(form)) {
    return {
      status: 'not_applicable',
      mode: null,
      message: '此表單不是苗林客戶建檔 mock workflow。',
    }
  }

  const workflowChoice = getAnswer(answers, 'q-miaolin-01')
  if (workflowChoice === '全新客戶') return checkNewCustomer(answers)
  if (workflowChoice === '既有客戶變更資料') return checkExistingCustomerChange(answers)

  return {
    status: 'not_applicable',
    mode: null,
    message: '尚未選擇新客戶建檔類型。',
  }
}
